import { useCallback, useEffect, useState } from 'react';
import { generatePath } from 'react-router';
import { makeStyles } from '../../../themes';
import { useFormik } from 'formik';
import { object, string } from 'yup';
import { FormModal } from './FormModal';
import { TextInput } from '../inputs';
import { addResource, deleteResource, useDispatch } from '../../../state';
import { authGet } from '../../../lib';
import { CustomerCartRoverKeyAPI, CustomerWarehouseAPI } from '../../../types';
const { VITE_SERVICES_BASE_URL } = import.meta.env;

const webhookUrl = `${VITE_SERVICES_BASE_URL}/webhooks/cart-rover/customer/:customerID/warehouse/:warehouseID`;

interface FormValues {
  id?: number;
  cartRoverID: string;
}

interface Props {
  open: boolean;
  handleClose: () => any;
  customerWarehouse: CustomerWarehouseAPI;
}

export function CustomerWarehouseCartRoverKeysForm({
  open,
  handleClose,
  customerWarehouse,
}: Props) {
  const { classes } = useStyles();
  const dispatch = useDispatch();

  const [state, setState] = useState<FormValues>({
    cartRoverID: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const { customerID, warehouseID } = customerWarehouse || {};

  useEffect(() => {
    (async function () {
      if (customerID && warehouseID) {
        const { data } = await authGet<CustomerCartRoverKeyAPI>(
          `customers/${customerID}/warehouse/${warehouseID}/cart-rover-key`,
        );
        if (data) {
          setState((cur) => ({
            ...cur,
            ...data,
            cartRoverID: data.cartRoverID,
          }));
        }
      }
    })();
  }, [customerID, warehouseID]);

  const handleSubmit = useCallback(
    async (values: FormValues) => {
      setSubmitting(true);

      const payload: CustomerCartRoverKeyAPI = {
        customerID,
        warehouseID,
        cartRoverID: values.cartRoverID,
      };

      const { error } = await dispatch(
        addResource<CustomerCartRoverKeyAPI>({
          baseUrl: `/customers/${customerID}/cart-rover-key`,
          payload,
          message: 'Changes saved',
        }),
      );
      setSubmitting(false);
      if (!error) {
        handleClose();
      }
    },
    [dispatch, handleClose, customerID, warehouseID],
  );

  const handleDelete = useCallback(async () => {
    setSubmitting(true);
    const { error } = await dispatch(
      deleteResource<CustomerCartRoverKeyAPI>({
        baseUrl: `/customers/${customerID}/warehouse`,
        id: warehouseID,
        path: 'cart-rover-key',
      }),
    );
    setSubmitting(false);
    if (!error) {
      handleClose();
    }
  }, [dispatch, handleClose, customerID, warehouseID]);

  const formik = useFormik<FormValues>({
    initialValues: state,
    enableReinitialize: true,
    onSubmit: handleSubmit,
    validationSchema: schema,
  });

  return (
    <FormModal
      open={open}
      handleClose={handleClose}
      title='Customer warehouse settings'
      callback={formik.handleSubmit}
      btnText='SAVE'
      typeSubmit
      paperProps={{ style: { width: 468 } }}
      submitting={submitting}
      isEdit={!!state.id}
      handleDelete={handleDelete}
    >
      <TextInput
        name='cartRoverID'
        formikProps={formik}
        label='CartRover ID'
        required
        className={classes.input}
        slotProps={{ htmlInput: { autoComplete: 'new-password' } }}
      />
      {!!customerID && !!warehouseID && (
        <div className={classes.webhookUrl}>
          <span>Webhook URL:</span>
          <span>
            {generatePath(webhookUrl, { customerID, warehouseID }) +
              '?token={secret token}'}
          </span>
        </div>
      )}
    </FormModal>
  );
}

const useStyles = makeStyles({ name: { CustomerWarehouseCartRoverKeysForm } })(
  (theme) => ({
    title: {
      fontFamily: 'Montserrat',
      fontSize: 20,
      fontWeight: 600,
    },
    input: {
      marginBottom: 16,
      width: '100%',
    },
    webhookUrl: {
      display: 'flex',
      flexDirection: 'column',
      gap: 8,
    },
  }),
);

const schema = object({
  cartRoverID: string().required('CartRover ID is required'),
});
